import {
  CHANGE_MODAL_VISIBILITY,
  NAVIGATING,
  IS_EDITING_FORM,
  CONFIRM_ROUTE_CHANGE,
  SET_DESTINATION_ROUTE,
  CLEAR_STATE,
  SET_INIT_DATA_LOADING_STATUS,
  SET_INIT_DATA_LOADED_STATUS
} from '../mutationTypes'
import { isModelId } from '@/utils/helpers/isId'
import { buildInitPromiseIterable } from '@/utils/helpers/BuildPromiseIterable'
import { clearState } from '@/store/modules/helpers/mutationHelpers'

const state = {
  createModalVisible: {},
  navigating: false,
  saving: false,
  isEditingForm: false,
  confirmRouteLeave: false,
  destinationRoute: null,
  initDataLoading: false,
  initDataLoaded: false
}

const actions = {                                                               // TODO: needs refactoring/simplification
  findDependencies (store, payload) {
    if (!payload || !Object.keys(payload).length) return []
    let entities
    if (Object.keys(payload).includes('entities')) {
      entities = payload.entities
    } else {
      entities = payload
    }
    let missing = {}                                                            // this will hold the master list of all missing entities
    for (let e in entities) {                                                   // First, enumerate entities
      if (!entities.hasOwnProperty(e)) continue
      let deps = {}
      const models = entities[e]                                                // Get an individual models for a given type
      for (let m in models) {                                                   // Then, for each prop in the current mode
        const modelProps = models[m]                                            // Grab value of the current prop of the current models
        for (let p in modelProps) {                                             // Go through each of its properties and determine whether the value is an integer or a collection/iterable containing only integers
          if (!modelProps.hasOwnProperty(p) ||
            (!isModelId(modelProps[p]) || p === 'id' || p === 'lines')) {       // We don't want to include the modelProps's own id, and if p is 'lines', we want to skip that because lines are automatically returned with the response
            continue
          }
          if (!deps.hasOwnProperty(p)) deps[p] = []                             // If deps doesn't contain an array for this property yet, create one
          if (!deps[p].includes(modelProps[p])) deps[p].push(modelProps[p])     // Check to make sure model id is not already in list
        }
      }
      if (Object.keys(deps).length) {                                           // At this point, you should have an object full of properties whose values are, or contain, integer(s) or UUID(s)
        const missingForCurrentEntity = store.getters.getMissingDataIds(deps)   // So now you need to go through each property and get the IDs of the missing data points
        for (let m in missingForCurrentEntity) {                                // And find out if each value (in this case, each property value's pk) exists in the value's respective state object
          if (!missingForCurrentEntity.hasOwnProperty(m)) continue              // If it doesn't, you need to add it to the list of that property's values that need to be fetched
          if (!missing.hasOwnProperty(m)) missing[m] = []
          if (!missing[m].includes(missingForCurrentEntity[m])) {
            missingForCurrentEntity[m].map(v => {
              missing[m].push(v)
            })
          }
        }
      }
    }

    const mergeArraysAndRemoveDuplicates = (obj) => {
      const result = {}
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          if (key.includes('address')) {
            result.address = [...new Set((result.address || []).concat(obj[key]))]
          } else if (Array.isArray(obj[key])) {
            result[key] = [...new Set(obj[key])]
          } else {
            result[key] = obj[key]
          }
        }
      }
      return result
    }
    // console.log(removeDupes(missing))
    return buildInitPromiseIterable(store, mergeArraysAndRemoveDuplicates(missing))
  },
  changeModalVisibility (store, payload) {
    this.commit(CHANGE_MODAL_VISIBILITY, payload)
  },
  clearState (store) {
    this.commit(CLEAR_STATE)
  },
  trackNavigation (store, payload) {
    this.commit(NAVIGATING, payload)
  },
  isEditingForm (store, payload) {
    this.commit(IS_EDITING_FORM, payload)
  },
  confirmRouteLeave (store, payload) {
    this.commit(CONFIRM_ROUTE_CHANGE, payload)
    return true
  },
  setDestinationRoute (store, payload) {
    if (payload && (payload.name || payload.path)) {
      this.commit(SET_DESTINATION_ROUTE, payload)
      return true
    }
    return false
  },
  setInitDataLoadingStatus (store, payload) {
    this.commit(SET_INIT_DATA_LOADING_STATUS, payload)
  },
  setInitDataLoadStatus (store, payload) {
    this.commit(SET_INIT_DATA_LOADED_STATUS, payload)
  }
}

const getters = {
  getMissingDataIds: (state, rootState) => (deps) => {
    let missingData = {}
    for (let d in deps) {
      if (!deps.hasOwnProperty(d)) continue
      const fieldsToSkip = [                                                    // Skip these false positives
        'currency',
        'period',
        'line_number',
        'referencing_line',
        'payment_check_split',
        'payment_combine_checks',
        'payment_confirmation_number',
        'offset_by',
        'open_pos',
        'page',
        'due_days',
        'check_copies',
        'check_range_start',
        'check_range_end',
        'eft_range_start',
        'eft_range_end',
        'transaction',
        'transaction_id',
        'related_transactions',
        'meta_name',
        'ledger',
        'ledger_id',
        'w9',
        'export',
        'check_offset_x',
        'check_offset_y',
        'files',
        'company',
        'production',
        'bank',
        'location',
        'series',
        'set',
        'insurance',
        'tax_code',
        'state',
        'item',
        'amount_owed',
        'deposit_account',
        'account',
        'account_clearing',
        'account_cash',
        'parent',
        'default_accounts',
        'default_account_cash',
        'default_account_clearing',
        'default_account_deposits',
        'default_account_discount',
        'default_tax_code',
        'petty_cash_account',
        'receivable_account',
        'petty_cash_custodian_account',
        'free_field_1',
        'free_field_2',
        'free_field_3',
        'free_field_4',
        'payment_term',
        'sales_rep',
        'user',
        'meta_data',
        'state',
        'work_state',
        'default_free_field_1',
        'default_free_field_2',
        'default_free_field_3',
        'default_free_field_4',
        'default_insurance',
        'default_location',
        'default_series',
        'default_set',
        'default_tax_code',
        'contact_title',
        'jurisdiction'
      ]
      if (fieldsToSkip.includes(d)) continue
      let common                                                                // Figure out which values deps[d] and its corresponding state object have in common
      switch (d) {                                                              // I.e. which data already exists
        case 'budget':
        case 'line':
        case 'period':
        case 'po':
          common = rootState[`get${d.charAt(0).toUpperCase() + d.slice(1)}s`].filter(v => deps[d].includes(v.id))
          break
        // case 'state':
        // case 'work_state':
        //   common = rootState.getStates.filter(v => deps[d].includes(v.id))
        //   break
        case 'address':
        case 'address_physical':
        case 'address_mailing':
        case 'address_selected':
        case 'address_contact':
        case 'address_billing':
          common = rootState.getAddresses.filter(v => deps[d].includes(v.id))
          break
        case 'city':
          common = rootState.getCities.filter(v => deps[d].includes(v.id))
          break
        case 'ar':
        case 'bill':
        case 'limit_to_bid':
          common = rootState.getArs.filter(v => deps[d].includes(v.id))
          break
        case 'prepayment':
          common = rootState.getPps.filter(v => deps[d].includes(v.id))
          break
        case 'batch':
          common = rootState.getBatches.filter(v => deps[d].includes(v.id))
          break
        case 'country':
          common = rootState.getCountries.filter(v => deps[d].includes(v.id))
          break
        // case 'currency':
        //   common = rootState.getCurrencies.filter(v => deps[d].includes(v.id))
        //   break
        case 'customer':
          common = rootState.getCustomers.filter(v => deps[d].includes(v.id))
          break
        case 'pettycash':
          common = rootState.getPcs.filter(v => deps[d].includes(v.id))
          break
        case 'recon':
          common = rootState.getBankRecs.filter(v => deps[d].includes(v.id))
          break
        case 'source_code':
          common = rootState.getSourcecodes.filter(v => deps[d].includes(v.id))
          break
        case 'parent':
        case 'third_party_vendor':
        case 'vendor':
          common = rootState.getVendorsCache.filter(v => deps[d].includes(v.id))
          break
        case 'transaction':
        case 'transaction_id':
          common = rootState.getTransactions.filter(v => deps[d].includes(v.id))
          break
        case 'relieves':
          common = rootState.getPoLines.filter(v => deps[d].includes[v.id])
          break
        case 'period_id':
          common = rootState.getPeriods.filter(v => deps[d].includes[v.id])
          break
      }
      if (common === undefined) {
        console.log('Could not assign getter to dependency - ' + d)
        // Now that the warning has been displayed, ignore for now as these are likely new fields from API
        // This will cause conflicts as it Ian has code to fix this in his branch, so resolve by taking his.
        common = []
      }
      deps[d].map(v => {                                                      // Now you need to get the difference between what's in deps[d] and common
        if (!common.filter(c => c.id === v).length) {
          if (!missingData.hasOwnProperty(d)) missingData[d] = []
          missingData[d].push(v)
        }
      })
    }
    return missingData
  },
  getModalVisibilityState: (state) => state.createModalVisible || {},
  getNavigationStatus: (state) => state.navigating,
  getIsUserEditingForm: (state) => state.isEditingForm,
  getRouteLeaveConfirmation: (state) => state.confirmRouteLeave,
  getDestinationRoute: (state) => state.destinationRoute,
  getInitDataLoadingStatus: (state) => state.initDataLoading,
  getInitDataLoadedStatus: (state) => state.initDataLoaded
}

const mutations = {
  [CHANGE_MODAL_VISIBILITY] (state, payload) {
    if (!payload) {
      state.createModalVisible = Object.assign({})
    } else {
      let { entityType, visible, inDataEntryView, type } = payload
      let tmp = Object.assign({}, state.createModalVisible)
      if (!inDataEntryView) {
        inDataEntryView = false
      }
      tmp[entityType] = { visible, inDataEntryView, type }
      state.createModalVisible = Object.assign({}, tmp)
    }
  },
  [NAVIGATING] (state, payload) {
    state.navigating = payload
  },
  [IS_EDITING_FORM] (state, payload) {
    state.isEditingForm = payload || false
  },
  [CONFIRM_ROUTE_CHANGE] (state, payload) {
    state.confirmRouteLeave = payload || false
  },
  [SET_DESTINATION_ROUTE] (state, payload) {
    state.destinationRoute = payload || null
  },
  [SET_INIT_DATA_LOADING_STATUS] (state, payload) {
    state.initDataLoading = payload || false
  },
  [SET_INIT_DATA_LOADED_STATUS] (state, payload) {
    state.initDataLoaded = payload || false
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/ui.js